/*
  pillar-hero-slideshow.js
  Auto-advancing image rotator for the pillar hero's media frame
  (sections/main-pillar.liquid → .pillar-hero__media).

  Built on the pattern proven by <hero-slideshow> (assets/hero-homepage.js):
  hidden radio inputs are the state machine and the `:checked ~` rules that move
  the rail are generated in the section's scoped {% style %} block. That means
  the DOTS WORK WITHOUT THIS FILE — JS only adds the timer and the wrap. Deliberately
  a separate element rather than a reuse of <hero-slideshow>, whose querySelectors are
  bound to the hero-homepage__ BEM namespace; sharing it would either leak those class
  names into the pillar or force a refactor of the homepage's LCP hero for a blog page.

  Markup contract (see snippets/pillar-hero-slide.liquid for a slide):

    <pillar-hero-slideshow class="pillar-hero__media" data-autoplay="5000">
      <div class="pillar-hero__frame">
        <input type="radio" class="pillar-hero__radio" checked>   ← one per REAL slide
        <input type="radio" class="pillar-hero__radio">
        <div class="pillar-hero__rail">
          <img> <img>          ← the real slides
          <img aria-hidden>    ← trailing clone of slide 1 (see "The wrap" below)
        </div>
      </div>
      <div class="pillar-hero__dots"><label …>×N</label></div>
    </pillar-hero-slideshow>

  The rail is a transform track, NOT a scroll container — there is no scrollLeft
  anywhere in this file, so rules/javascript.md → Scroll-Track CSS Contract does
  not apply to it. (That contract governs overflow-x:auto tracks such as
  <infinite-carousel>'s, which this deliberately is not.)

  The wrap — why a clone exists:
  A plain rail rewinds when it loops. Advancing from the last slide back to index 0
  animates the whole rail backwards past every image; with just two images it reads
  as a back-and-forth ping-pong rather than a carousel. So the rail carries a
  trailing duplicate of slide 1: autoplay slides FORWARD into the clone, then — with
  transitions suppressed for one frame — snaps back to the real slide 1. Both frames
  show the same picture, so the reset is invisible and the motion is always leftward.

  Swipe (≤750px): touchstart/touchend on .pillar-hero__frame, no touchmove — the
  same shape as hero-homepage.js's <hero-slideshow> swipe. Forward swipes (step(1))
  just call advance(), reusing its clone-wrap. Backward swipes (step(-1)) move back
  one slide directly and do nothing at slide 1 — there's no leading clone, and a
  single adjacent-index step is already safe with no rewind (a dot click proves
  that); only the forward end-of-loop case needs the clone treatment.

  Autoplay pauses on hover, on focus, and while the tab is hidden, and never starts
  at all under prefers-reduced-motion. WCAG 2.2.2 note: pause-on-hover/focus is not
  a persistent pause control, but the dots are real focusable radios here (they use
  .visually-hidden, not display:none), so a keyboard user reaching them halts the
  rotation — which the homepage hero's display:none radios do not allow.
*/
class PillarHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.radios = [...this.querySelectorAll('.pillar-hero__radio')];
    this.rail = this.querySelector('.pillar-hero__rail');
    this.frame = this.querySelector('.pillar-hero__frame');

    // Single-image pillars render the same element with one slide and no radios.
    // Nothing to rotate — leave it as an inert frame.
    if (this.radios.length < 2 || !this.rail) return;

    this.ac = new AbortController();
    const { signal } = this.ac;

    // Swipe works independent of autoplay/reduced-motion — it's a direct user
    // gesture, not the decorative motion those settings are meant to gate.
    this._interval = parseInt(this.dataset.autoplay, 10) || 0;
    this.setupSwipe(signal);

    if (!this._interval) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._hovered = false;
    this._focused = false;

    // Any navigation — a dot click or the wrap below — restarts the countdown so
    // a slide the reader just chose gets its full dwell time.
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.startTimer(), { signal });
    });

    this.addEventListener('mouseenter', () => { this._hovered = true; this.pauseTimer(); }, { signal });
    this.addEventListener('mouseleave', () => { this._hovered = false; this.startTimer(); }, { signal });
    this.addEventListener('focusin', () => { this._focused = true; this.pauseTimer(); }, { signal });
    this.addEventListener('focusout', () => { this._focused = false; this.startTimer(); }, { signal });
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.pauseTimer() : this.startTimer()),
      { signal }
    );

    this.startTimer();
  }

  disconnectedCallback() {
    this.pauseTimer();
    this.ac?.abort();
  }

  /* ── Swipe ──────────────────────────────────────────────────────────────── */

  // Bound to .pillar-hero__frame specifically, not the whole element — the dots
  // row is a separate sibling below it now and shouldn't be part of the gesture
  // area. Start/end delta only, mirroring hero-homepage.js's <hero-slideshow>.
  setupSwipe(signal) {
    let startX = 0;
    let startY = 0;

    this.frame.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      if (this._interval) this.pauseTimer();
    }, { signal, passive: true });

    this.frame.addEventListener('touchend', (event) => {
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (
        window.matchMedia('(max-width: 750px)').matches &&
        Math.abs(dx) > 40 &&
        Math.abs(dx) > Math.abs(dy)
      ) {
        this.step(dx < 0 ? 1 : -1);
      }
      if (this._interval) this.startTimer();
    }, { signal, passive: true });
  }

  // delta > 0 (swipe left, next slide): advance() already handles the forward
  // step and the trailing-clone wrap at the end of the set.
  // delta < 0 (swipe right, previous slide): step back one directly; no-op at
  // slide 1 rather than wrapping — see the Swipe note in the header comment.
  step(delta) {
    if (delta > 0) {
      this.advance();
      return;
    }

    const current = this.radios.findIndex((radio) => radio.checked);
    if (current <= 0) return;

    const prev = this.radios[current - 1];
    prev.checked = true;
    prev.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Timer ──────────────────────────────────────────────────────────────── */

  startTimer() {
    this.pauseTimer();
    // Don't resume under the pointer or while focus is inside — otherwise clicking
    // a dot would restart rotation while the reader is still interacting.
    if (this._hovered || this._focused || document.hidden) return;
    this._timer = setTimeout(() => this.advance(), this._interval);
  }

  pauseTimer() {
    clearTimeout(this._timer);
    this._timer = null;
    this.cancelWrap();
  }

  /* ── Advance + wrap ─────────────────────────────────────────────────────── */

  advance() {
    const current = this.radios.findIndex((radio) => radio.checked);
    if (current === -1) return;

    if (current < this.radios.length - 1) {
      const next = this.radios[current + 1];
      next.checked = true;
      // Dispatched so the change listener above restarts the countdown, exactly
      // as it would for a reader-initiated dot click.
      next.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Last real slide → slide forward into the trailing clone. An inline custom
    // property outranks the :checked ~ .pillar-hero__rail rule from the section's
    // style block, so the rail keeps moving left past the end of the real set.
    this.rail.style.setProperty('--pillar-hero-i', this.radios.length);
    this._wrapTimer = setTimeout(() => this.resetToFirst(), this.slideDuration());
  }

  // Silently return to the real slide 1. The clone shows the same picture, so
  // suppressing the transition for one frame makes the jump invisible.
  resetToFirst() {
    this._wrapTimer = null;
    this.setAttribute('data-instant', '');
    this.rail.style.removeProperty('--pillar-hero-i');
    this.radios[0].checked = true;
    // Force a synchronous layout flush so the untransitioned jump is committed
    // before transitions are re-enabled. A bare rAF can batch both mutations into
    // one frame, which animates the rewind we built the clone to avoid.
    void this.rail.offsetWidth;
    this.removeAttribute('data-instant');
    this.startTimer();
  }

  // Abort an in-flight wrap and hand control back to whichever radio is checked.
  // Without this a dot clicked during the ~600ms wrap window would be overridden
  // by the inline custom property, and then stomped by resetToFirst().
  cancelWrap() {
    if (!this._wrapTimer) return;
    clearTimeout(this._wrapTimer);
    this._wrapTimer = null;
    this.rail.style.removeProperty('--pillar-hero-i');
  }

  // Read the duration off the rail so the wrap timing tracks the CSS rather than
  // duplicating the number here.
  slideDuration() {
    const declared = (getComputedStyle(this.rail).transitionDuration || '').split(',')[0].trim();
    if (declared.endsWith('ms')) return parseFloat(declared) || 600;
    if (declared.endsWith('s')) return (parseFloat(declared) || 0.6) * 1000;
    return 600;
  }
}

if (!customElements.get('pillar-hero-slideshow')) {
  customElements.define('pillar-hero-slideshow', PillarHeroSlideshow);
}
